import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { FaChevronDown } from 'react-icons/fa';

/**
 * Select variants using CVA
 */
export const selectVariants = cva(
  'flex w-full items-center justify-between rounded-lg border bg-[var(--surface-base)] text-[var(--text-primary)] px-3 py-2 text-sm transition-all duration-normal focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 appearance-none cursor-pointer',
  {
    variants: {
      variant: {
        default:
          'border-[var(--border-secondary)] focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20',
        error:
          'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20',
      },
      selectSize: {
        sm: 'h-9 text-sm',
        md: 'h-10 text-sm',
        lg: 'h-12 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      selectSize: 'md',
    },
  }
);

/**
 * Select Component
 *
 * @param {Object} props
 * @param {'default'|'error'} [props.variant='default']
 * @param {'sm'|'md'|'lg'} [props.selectSize='md']
 * @param {string} [props.placeholder]
 * @param {string} [props.className]
 */
const Select = React.forwardRef(({
  className,
  variant,
  selectSize,
  children,
  placeholder,
  ...props
}, ref) => {
  return (
    <div className="relative">
      <select
        ref={ref}
        className={cn(
          selectVariants({ variant, selectSize }),
          'pr-10', // Make room for the icon
          className
        )}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {children}
      </select>
      <FaChevronDown
        className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-tertiary)] pointer-events-none"
      />
    </div>
  );
});

Select.displayName = 'Select';

/**
 * SelectOption Component (just a styled option)
 */
const SelectOption = React.forwardRef(({ className, ...props }, ref) => (
  <option ref={ref} className={className} {...props} />
));

SelectOption.displayName = 'SelectOption';

export { Select, SelectOption };
export default Select;
